import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ImportPreviewResponse,
  ImportStartRequest,
  ImportJob,
  ImportError,
} from '@/types/import';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export interface UseLeadImportReturn {
  uploadFile: (file: File) => Promise<ImportPreviewResponse>;
  startImport: (config: ImportStartRequest) => Promise<{ jobId: string }>;
  cancelImport: (jobId: string) => Promise<void>;
  getJobStatus: (jobId: string) => Promise<ImportJob>;
  getJobErrors: (jobId: string, page: number) => Promise<{ errors: ImportError[]; total: number }>;
  importJobs: ImportJob[];
  isUploading: boolean;
  error: string | null;
  clearError: () => void;
}

export const useLeadImport = (): UseLeadImportReturn => {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch import jobs history
  const { data: importJobs = [] } = useQuery<ImportJob[]>({
    queryKey: ['importJobs'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/leads/import/jobs`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch import jobs');
      }
      return response.json();
    },
    staleTime: 30000,
  });

  // Upload file for preview
  const uploadFile = useCallback(async (file: File): Promise<ImportPreviewResponse> => {
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/leads/import/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const result = await response.json();
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload file';
      setError(message);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, []);

  // Start import process
  const startImport = useCallback(async (config: ImportStartRequest): Promise<{ jobId: string }> => {
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/leads/import/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to start import');
      }

      const result = await response.json();
      queryClient.invalidateQueries({ queryKey: ['importJobs'] });
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start import';
      setError(message);
      throw err;
    }
  }, [queryClient]);

  // Cancel import
  const cancelImport = useCallback(async (jobId: string): Promise<void> => {
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/leads/import/jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel import');
      }

      queryClient.invalidateQueries({ queryKey: ['importJobs'] });
      queryClient.invalidateQueries({ queryKey: ['importJobStatus', jobId] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel import';
      setError(message);
      throw err;
    }
  }, [queryClient]);

  // Get job status
  const getJobStatus = useCallback(async (jobId: string): Promise<ImportJob> => {
    const response = await fetch(`${API_BASE_URL}/leads/import/jobs/${jobId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch job status');
    }

    return response.json();
  }, []);

  // Get job errors
  const getJobErrors = useCallback(async (
    jobId: string,
    page: number
  ): Promise<{ errors: ImportError[]; total: number }> => {
    const response = await fetch(
      `${API_BASE_URL}/leads/import/jobs/${jobId}/errors?page=${page}&limit=10`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch job errors');
    }

    return response.json();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    uploadFile,
    startImport,
    cancelImport,
    getJobStatus,
    getJobErrors,
    importJobs,
    isUploading,
    error,
    clearError,
  };
};
